import { baseApi } from "./apiSingleton";

export type IInitConsortiaResponse = Array<{
  code: string;
  commission: null;
  id: number;
  name: string;
}>;

export type IInitMarketsResponse = Array<{
  code: string;
  id: number;
  name: string;
}>;

export type IInitRegulatoryResponse = Array<{
  code: string;
  id: number;
  name: string;
}>;

export interface ICreateAgencyRequest {
  sales_area: null;
  api_accounts: string[];
  name: string;
  address: Array<{ name: string; value: string }>;
  telephone: string;
  website: string;
  account_type: string;
  consortia: string;
  regulatory: Record<string, string>;
  market: string;
  agent_address: string;
  email: string;
  b2b_password: string;
  code: string;
  commission_level: number;
  credit_limit: number;
}

export interface ICreateAgentRequest {
  address: Array<{ name: string; value: string }>;
  sales_area: null;
  first_name?: string;
  last_name?: string;
  title?: string;
  telephone: string;
  agency: number;
  position: string;
  email: string;
  b2b_password: string;
  code: string;
  name?: string;
  registration_consent?: boolean;
  marketing_opt_in?: boolean;
}

export type ISearchAgencyResponse = Array<Record<string, string>>;
export type ISearchAgentResponse = Array<Record<string, string>>;

export interface ISearchRegulatoryRequest {
  code: string;
  value: string;
}

export type ISearchRegulatoryResponse = {
  value?: string;
  regulatory?: string;
  code?: string;
  id: string;
  name: string;
};

export interface IGetAgencyDetailsResponse {
  account_type: "creditaccount" | "creditcard" | "both" | "";
  commission_level: string;
  created_by: string;
  market: string;
}

export interface IGetAgentDetailsResponse {
  id: number;
  code: string;
  uuid: string;
  name: string;
  title: string;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  address: Array<{ name: string; value: string }>;
  telephone: string | null;
  email: string;
  b2b_password: string;
  external_code: string;
  active: boolean;
  booking_access: string;
  agency: number;
  known_as: string;
  type_of_agent: string;
  is_default: boolean;
  is_web: boolean;
  sales_area: string | null;
  send_customer_emails: boolean;
  switch_agent: boolean;
  can_book_hotels: boolean;
}

export const AgencyAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    initConsortia: builder.query<IInitConsortiaResponse, null>({
      query: () => ({
        url: "/cnf/consortia/",
        method: "GET",
      }),
    }),
    initMarkets: builder.query<IInitMarketsResponse, null>({
      query: () => ({
        url: "/cnf/market/",
        method: "GET",
      }),
    }),
    initRegulatory: builder.query<IInitRegulatoryResponse, null>({
      query: () => ({
        url: "/cnf/regulatory/",
        method: "GET",
      }),
    }),
    createAgency: builder.query<{ id: number }, ICreateAgencyRequest>({
      query: (body) => ({
        url: "/ops/agency/",
        method: "POST",
        body,
      }),
    }),
    createAgent: builder.query<null, ICreateAgentRequest>({
      query: (body) => ({
        url: "/ops/agent/",
        method: "POST",
        body,
        responseHandler: async (response) => {
          const text = await response.text();
          try {
            return JSON.parse(text);
          } catch {
            return text;
          }
        },
      }),
    }),
    searchAgencies: builder.query<ISearchAgencyResponse, null>({
      query: () => ({
        url: "/ops/agency/",
        method: "GET",
      }),
    }),
    searchAgents: builder.query<ISearchAgentResponse, null>({
      query: () => ({
        url: "/ops/agent/",
        method: "GET",
      }),
    }),
    getAgencyDetails: builder.query<IGetAgencyDetailsResponse, string>({
      query: (agencyId) => ({
        url: `/ops/agency/${agencyId}`,
        method: "GET",
      }),
    }),
    getAgentDetails: builder.query<IGetAgentDetailsResponse, string>({
      query: (agentId) => ({
        url: `ops/agent/${agentId}`,
        method: "GET",
      }),
    }),
    searchRegulatory: builder.query<
      ISearchRegulatoryResponse,
      ISearchRegulatoryRequest
    >({
      query: ({ code, value }) => ({
        url: `/ops/agency/search-regulatory/?code=${encodeURIComponent(
          code,
        )}&value=${encodeURIComponent(value)}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useLazySearchAgenciesQuery,
  useLazySearchAgentsQuery,
  useLazySearchRegulatoryQuery,
  useLazyGetAgentDetailsQuery,
} = AgencyAPI;
